import React, { FC, ReactNode } from 'react';
import { FiCheck, FiMinus } from 'react-icons/fi';
import cx from 'classnames';
import { APP_ACCENT, APP_SEMANTIC } from '@/constant/theme/colors.constant';
import { Text } from './AppText';

// Usage
// -----
// Basic:         <AppCheckbox checked={val} onChange={setVal} />
// Label:         <AppCheckbox checked={val} onChange={setVal} label="Remember me" />
// Label left:    <AppCheckbox checked={val} onChange={setVal} label="Terms" labelPosition="left" />
// Sizes:         xs | sm | md (default) | lg
// Colors:        primary (default) | blue | purple | green | red | orange | teal
// Variants:      filled (default) | outlined
// Indeterminate: <AppCheckbox checked={false} onChange={setVal} indeterminate />
// Disabled:      <AppCheckbox checked={val} onChange={setVal} disabled />

export type CheckboxSize = 'xs' | 'sm' | 'md' | 'lg';
export type CheckboxColor =
  | 'primary'
  | 'blue'
  | 'purple'
  | 'green'
  | 'red'
  | 'orange'
  | 'teal';
export type CheckboxVariant = 'filled' | 'outlined';

export interface AppCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  label?: ReactNode;
  labelPosition?: 'left' | 'right';
  size?: CheckboxSize;
  color?: CheckboxColor;
  variant?: CheckboxVariant;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Render with white styling — use when placed on a filled/colored background */
  inverted?: boolean;
}

const SIZE: Record<
  CheckboxSize,
  { box: string; icon: string; text: 'sm' | 'md' | 'base' | 'lg'; gap: string }
> = {
  xs: {
    box: 'w-3.5 h-3.5 rounded',
    icon: 'w-2.5 h-2.5',
    text: 'sm',
    gap: 'gap-1.5',
  },
  sm: { box: 'w-4 h-4 rounded', icon: 'w-3 h-3', text: 'md', gap: 'gap-2' },
  md: {
    box: 'w-[18px] h-[18px] rounded',
    icon: 'w-3.5 h-3.5',
    text: 'base',
    gap: 'gap-2.5',
  },
  lg: { box: 'w-5 h-5 rounded-md', icon: 'w-4 h-4', text: 'lg', gap: 'gap-3' },
};

const FILLED_BG: Record<CheckboxColor, string> = {
  primary: APP_ACCENT.primary.bg,
  blue: APP_ACCENT.blue.bg,
  purple: APP_ACCENT.purple.bg,
  green: APP_ACCENT.green.bg,
  red: APP_ACCENT.red.bg,
  orange: APP_ACCENT.orange.bg,
  teal: APP_ACCENT.teal.bg,
};

const FILLED_RING: Record<CheckboxColor, string> = {
  primary: APP_ACCENT.primary.ring,
  blue: APP_ACCENT.blue.ring,
  purple: APP_ACCENT.purple.ring,
  green: APP_ACCENT.green.ring,
  red: APP_ACCENT.red.ring,
  orange: APP_ACCENT.orange.ring,
  teal: APP_ACCENT.teal.ring,
};

const OUTLINED_BORDER: Record<CheckboxColor, string> = {
  primary: APP_ACCENT.primary.border,
  blue: APP_ACCENT.blue.border,
  purple: APP_ACCENT.purple.border,
  green: APP_ACCENT.green.border,
  red: APP_ACCENT.red.border,
  orange: APP_ACCENT.orange.border,
  teal: APP_ACCENT.teal.border,
};

const OUTLINED_ICON: Record<CheckboxColor, string> = {
  primary: APP_ACCENT.primary.text,
  blue: APP_ACCENT.blue.text,
  purple: APP_ACCENT.purple.text,
  green: APP_ACCENT.green.text,
  red: APP_ACCENT.red.text,
  orange: APP_ACCENT.orange.text,
  teal: APP_ACCENT.teal.text,
};

const FOCUS_RING: Record<CheckboxColor, string> = {
  primary: 'focus-visible:ring-primary/40',
  blue: 'focus-visible:ring-blue-500/40',
  purple: 'focus-visible:ring-purple-500/40',
  green: 'focus-visible:ring-green-500/40',
  red: 'focus-visible:ring-red-500/40',
  orange: 'focus-visible:ring-orange-500/40',
  teal: 'focus-visible:ring-teal-500/40',
};

const AppCheckbox: FC<AppCheckboxProps> = ({
  checked,
  onChange,
  indeterminate = false,
  label,
  labelPosition = 'right',
  size = 'md',
  color = 'primary',
  variant = 'filled',
  disabled = false,
  className,
  id,
  inverted = false,
}) => {
  const s = SIZE[size];
  const isActive = checked || indeterminate;
  const filled = variant === 'filled';

  const box = (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      id={id}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cx(
        'relative inline-flex flex-shrink-0 items-center justify-center transition-all duration-150 outline-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        FOCUS_RING[color],
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
    >
      <span
        className={cx(
          'flex items-center justify-center transition-all duration-150 border-2',
          s.box,
          inverted && !isActive && ['bg-white/10 border-white/50'],
          inverted && isActive && ['border-white/80 bg-white/20 scale-[1.04]'],
          !inverted &&
            filled &&
            !isActive && [
              'bg-white dark:bg-gray-800',
              'border-gray-300 dark:border-gray-600',
              APP_ACCENT.gray.hover,
            ],
          !inverted &&
            filled &&
            isActive && [
              'border-transparent scale-[1.04]',
              FILLED_BG[color],
              'ring-2',
              FILLED_RING[color],
            ],
          !inverted &&
            !filled &&
            !isActive && [
              'bg-transparent',
              'border-gray-300 dark:border-gray-500',
              APP_ACCENT.gray.hover,
            ],
          !inverted &&
            !filled &&
            isActive && ['bg-transparent', OUTLINED_BORDER[color]],
        )}
      >
        {isActive &&
          (indeterminate ? (
            <FiMinus
              className={cx(
                s.icon,
                inverted || filled ? 'text-white' : OUTLINED_ICON[color],
              )}
              strokeWidth={4}
            />
          ) : (
            <FiCheck
              className={cx(
                s.icon,
                inverted || filled ? 'text-white' : OUTLINED_ICON[color],
              )}
              strokeWidth={4}
            />
          ))}
      </span>
    </button>
  );

  if (!label) return box;

  return (
    <label
      className={cx(
        'inline-flex max-w-full flex-wrap items-center cursor-pointer select-none',
        s.gap,
        disabled && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      {labelPosition === 'left' && (
        <Text
          as="span"
          size={s.text}
          weight="medium"
          color="soft"
          leading="none"
          className="min-w-0 break-words"
        >
          {label}
        </Text>
      )}
      {box}
      {labelPosition === 'right' && (
        <Text
          as="span"
          size={s.text}
          weight="medium"
          color="soft"
          leading="none"
          className="min-w-0 break-words"
        >
          {label}
        </Text>
      )}
    </label>
  );
};

export default AppCheckbox;
